import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {Subject} from 'rxjs';
import {EventsApiService} from 'src/app/admin/events.service';
import {environment} from 'src/environments/environment';
import {ArtistService} from '../../services/artist.service';
import {debounceTime, distinctUntilChanged, switchMap} from 'rxjs/operators';

@Component({
    selector: 'app-artists-search',
    templateUrl: './artists-search.component.html',
    styleUrls: ['./artists-search.component.scss'],
    standalone: false
})
export class ArtistsSearchComponent implements OnInit, AfterViewInit, OnChanges {
  selectedUsers = [];
  musicStyles = [];
  searchUserName = '';
  dropdownUser = false;
  deployUrl;
  users;
  usersBase = [];
  lineupIds = new Set();
  systemUsers = [];
  excludeList = [];
  isProgrammaticFocus = false;
  dropdownReverse = false;
  filteredLineup: any[] = [];
  filteredSystem: any[] = [];
  highlightedIndex: number = -1;

  @Input() showChips = false;
  @Input() companyId;
  @Input() showSmallChips = false;
  @Input() showInInput = false;
  @Input() addMultipleUsers = false;
  @Input() showExcListInAdded = false;
  @Input() showUsersInLineup = false;
  @Input() showInSystem = true;
  @Input() customArtistsName = false;
  @Input() lineupArtists = [];
  @Input() excludeListIn = [];
  @Input() artistAndDaysArr = [];
  @Input() placeholder = 'Search for users';
  @Input() includedUsersPlaceholder = 'timetable.lineup_users';
  @Input() includedUsers: any[] = [];
  @Input() setTabindex: number;
  @Output() usersSelected = new EventEmitter();
  @Output() lastAddedArtist = new EventEmitter();
  @Output() artistRemoved = new EventEmitter();

  @ViewChild('dropdownMenu') dropdownMenu: ElementRef;
  @ViewChild('inputSearch') inputField: ElementRef;
  @ViewChild('inputCont') inputCont: ElementRef;
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;

  private inputValueChanged = new Subject<string>();

  constructor(
    private artistsService: ArtistService,
    private eventService: EventsApiService,
    private translateService: TranslateService,
  ) {}

  ngAfterViewInit(): void {
    this.checkDropdownPosition();
  }

  ngOnInit(): void {
    this.deployUrl = environment.deploy_url;
    if (this.showExcListInAdded) {
      this.selectedUsers = [...this.excludeListIn];
    }
    this.excludeList = [...this.excludeListIn];
    this.getMusicStyles();
    this.findUsersOnSearch();

    this.filteredLineup = this.lineupArtists
      .filter(item => !this.excludeList.some(ex => ex.id === item.id))
      .filter(item => item.name?.toLowerCase().includes(this.searchUserName.toLowerCase()));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.excludeListIn) {
      this.excludeList = [...changes.excludeListIn.currentValue];
      this.getUsers(false);
    }

    if (changes['lineupArtists'] && this.lineupArtists) {
      this.filteredLineup = this.lineupArtists
        .filter(item => !this.excludeList.some(ex => ex.id === item.id))
        .filter(item => item.name?.toLowerCase().includes(this.searchUserName.toLowerCase()));
    }
  }

  resetSearch() {
    this.getUsers(false);
    this.users = this.usersBase;
    this.selectedUsers = [];
  }

  resetLists() {
    this.filteredLineup = this.lineupArtists.filter(artist =>
      artist.name?.toLowerCase().includes(this.searchUserName.toLowerCase()),
    );
  }

  onFocus() {
    if (this.isProgrammaticFocus) {
      this.isProgrammaticFocus = false;
      return;
    }
    this.dropdownUser = true;
  }

  closeDropdown() {
    this.dropdownUser = false;
  }

  focusInputNoDropdown(): void {
    if (this.inputField) {
      this.isProgrammaticFocus = true;
      this.inputField.nativeElement.focus();
    }
  }

  getMusicStyles() {
    this.eventService.getMusicStyles().subscribe(data => {
      this.musicStyles = data.results;
    });
  }

  getMusicStyleName(style) {
    if (this.musicStyles.length) {
      return this.musicStyles.find(item => item.value === style)?.name;
    }
  }

  onInputValueChanged($event) {
    this.inputValueChanged.next(this.inputField.nativeElement.value);
  }

  findUsersOnSearch() {
    this.inputValueChanged
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((value: string) => this.artistsService.getCompanyArtistsV3(this.companyId, {name: value})),
      )
      .subscribe(data => {
        this.provideArtistsData(data, true);
      });
  }

  getUsers(showDropdown = true) {
    this.artistsService.getCompanyArtistsV3(this.companyId, {name: this.searchUserName}).subscribe((data: any) => {
      this.provideArtistsData(data, showDropdown);
    });
  }

  provideArtistsData(data, showDropdown) {
    if (this.lineupArtists && this.lineupArtists.length > 0) {
      this.lineupIds = new Set(this.lineupArtists.map(artist => artist.id));
    }

    const allUsers =
      this.excludeListIn.length > 0
        ? data.results.filter(item1 => !this.excludeList.some(item2 => item2.id === item1.id))
        : data.results;

    this.systemUsers = allUsers
      .filter(user => !this.lineupIds.has(user.id))
      .filter(item1 => !this.excludeList.some(item2 => item2.id === item1.id));

    this.users = allUsers;
    this.usersBase = this.users;

    if (showDropdown) {
      this.dropdownUser = true;
    }

    this.filteredLineup = this.lineupArtists
      .filter(artist => !this.excludeList.some(item => item.id === artist.id))
      .filter(artist => artist.name?.toLowerCase().includes(this.searchUserName.toLowerCase()));

    this.filteredSystem = [...this.systemUsers];
    this.handleExactMatches();
    this.checkDropdownPosition();
  }

  handleExactMatches(): void {
    this.highlightedIndex = -1;

    const lineupExactIndex = this.filteredLineup.findIndex(
      a => a.name?.toLowerCase() === this.searchUserName.toLowerCase(),
    );
    if (lineupExactIndex >= 0) {
      this.highlightedIndex = lineupExactIndex;
      return;
    }

    const systemExactIndex = this.filteredSystem.findIndex(
      a => a.name?.toLowerCase() === this.searchUserName.toLowerCase(),
    );
    if (systemExactIndex >= 0) {
      this.highlightedIndex = this.filteredLineup.length + systemExactIndex;
    }
  }

  returnArtistDays(dates) {
    return [...new Set(dates)];
  }

  returnArtistDaysList(dates: string[]): string {
    const uniqueDates = [...new Set(dates)];
    const totalPossibleDays = 7;

    if (uniqueDates.length === totalPossibleDays) {
      return this.translateService.instant('weekdays.all_days');
    }

    const weekOrder = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

    const dayData = uniqueDates.map(dateStr => {
      const dateObj = new Date(dateStr);
      const shortKey = dateObj.toLocaleDateString('en-US', {weekday: 'short'}).toLowerCase().slice(0, 3);

      const translated = this.returnDayName(shortKey, false);
      return {key: shortKey, translated};
    });

    dayData.sort((a, b) => weekOrder.indexOf(a.key) - weekOrder.indexOf(b.key));

    const dayNames = dayData.map(item => item.translated);
    return dayNames.join(', ');
  }

  returnDayName(dayKey: string, ifShorten = false): string {
    const key = 'weekdays.' + dayKey;
    const translatedDay = this.translateService.instant(key);
    return ifShorten ? translatedDay.slice(0, 3) : translatedDay;
  }

  checkDropdownPosition(): void {
    if (!this.inputField) {
      return;
    }

    setTimeout(() => {
      const inputRect = this.inputField.nativeElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownMenuHeight = 300;
      if (inputRect.bottom + dropdownMenuHeight > viewportHeight) {
        this.inputCont.nativeElement.classList.add('dropdown-up');
        this.dropdownReverse = true;
      } else {
        this.inputCont.nativeElement.classList.remove('dropdown-up');
        this.dropdownReverse = false;
      }
    }, 300);
  }

  @HostListener('window:resize')
  onResize(): void {
    this.checkDropdownPosition();
  }

  removeSelectedUser(user, index) {
    this.selectedUsers.splice(index, 1);

    this.excludeList = this.excludeList.filter(item => item.id !== user.id);
    this.users = [...this.usersBase.filter(item1 => !this.excludeList.some(item2 => item2.id === item1.id))];
    this.systemUsers = [...this.systemUsers.filter(item1 => !this.excludeList.some(item2 => item2.id === item1.id))];

    this.filteredLineup = this.lineupArtists
      .filter(item => !this.excludeList.some(item2 => item2.id === item.id))
      .filter(item => item.name?.toLowerCase().includes(this.searchUserName.toLowerCase()));

    if (this.systemUsers && this.systemUsers.length > 0) {
      this.filteredSystem = [
        ...this.systemUsers.filter(
          item =>
            !this.excludeList.some(item2 => item2.id === item.id) &&
            item.name?.toLowerCase().includes(this.searchUserName.toLowerCase()),
        ),
      ];
    }
    this.artistRemoved.emit(user);
  }

  setUser(user) {
    this.dropdownUser = false;
    this.searchUserName = '';

    if (!this.addMultipleUsers) {
      this.selectedUsers = [];
      this.excludeList = [...this.excludeListIn];
    }

    if ('country' in user) {
      const {id, name, profile_photo: thumbnail} = user;
      this.selectedUsers.push({id, name, thumbnail});
    } else {
      this.selectedUsers.push(user);
    }

    this.excludeList.push(user);

    this.filteredLineup = this.lineupArtists
      .filter(item => !this.excludeList.some(item2 => item2.id === item.id))
      .filter(item => item.name?.toLowerCase().includes(this.searchUserName.toLowerCase()));

    if (this.systemUsers && this.systemUsers.length > 0) {
      this.filteredSystem = [
        ...this.systemUsers.filter(
          item =>
            !this.excludeList.some(item2 => item2.id === item.id) &&
            item.name?.toLowerCase().includes(this.searchUserName.toLowerCase()),
        ),
      ];
    }

    this.usersSelected.emit(this.selectedUsers);
    this.lastAddedArtist.emit(user);
  }

  focusInput(inputElement: HTMLInputElement): void {
    if (inputElement) {
      inputElement.focus();
      setTimeout(() => {
        this.checkDropdownPosition();
        this.dropdownUser = true;
      });
    }
  }

  getImagePath() {
    return this.deployUrl + 'assets/icons/close-black.png';
  }

  onKeyDown(event: KeyboardEvent): void {
    if (!this.dropdownUser) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.dropdownReverse ? this.moveHighlightUp() : this.moveHighlightDown();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.dropdownReverse ? this.moveHighlightDown() : this.moveHighlightUp();
        break;
      case 'Enter':
        event.preventDefault();
        this.selectHighlightedItem();
        break;
      default:
        break;
    }
  }

  moveHighlightDown(): void {
    const total = this.totalFilteredCount;
    if (total === 0) {
      this.highlightedIndex = 0;
      return;
    }
    this.highlightedIndex = (this.highlightedIndex + 1) % total;
  }

  moveHighlightUp(): void {
    const total = this.totalFilteredCount;
    if (total === 0) {
      this.highlightedIndex = 0;
      return;
    }
    if (this.highlightedIndex === -1) {
      this.highlightedIndex = total - 1;
    } else {
      this.highlightedIndex = (this.highlightedIndex - 1 + total) % total;
    }
  }

  selectHighlightedItem(): void {
    const trimmedInput = this.searchUserName.trim();
    const allAvailableUsers = [...(this.users || []), ...(this.filteredLineup || []), ...(this.filteredSystem || [])];

    const userExists = allAvailableUsers.some(user => user.name.toLowerCase() === trimmedInput.toLowerCase());

    if (this.customArtistsName && !userExists && trimmedInput !== '' && this.highlightedIndex < 0) {
      const newUser = {
        id: null,
        days: [],
        name: trimmedInput,
        thumbnail: '',
      };
      this.selectedUsers.push(newUser);
      this.usersSelected.emit(this.selectedUsers);

      this.searchUserName = '';
    } else if (this.highlightedIndex < 0 || this.highlightedIndex >= this.totalFilteredCount) {
      return;
    }
    let selectedArtist;
    if (this.highlightedIndex < this.filteredLineup.length) {
      selectedArtist = this.filteredLineup[this.highlightedIndex];
    } else {
      const systemIndex = this.highlightedIndex - this.filteredLineup.length;
      selectedArtist = this.filteredSystem[systemIndex];
    }
    if (selectedArtist) {
      this.setUser(selectedArtist);
    }
    if (this.customArtistsName) {
      this.resetLists();
    }
  }

  get totalFilteredCount(): number {
    return this.filteredLineup.length + this.filteredSystem.length;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    const clickedInsideDropdown = this.dropdownMenu?.nativeElement.contains(event.target);
    const clickedInsideInput = this.inputField?.nativeElement.contains(event.target);

    if (!clickedInsideDropdown && !clickedInsideInput) {
      this.dropdownUser = false;
    }
  }
}
